import React, { useState, useEffect } from "react";
import {
  Dialog,
  IconButton,
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { ReactComponent as CloseIcon } from "../../../../assets/images/x.svg";
import Input from "../../../common/InputField";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { updateKbCategory } from "../../../../store/features/updateCategoryToKb";
import { useDispatch } from "react-redux";
import { getAllKbCategories } from "../../../../store/features/getCategoriesToKnowledgeBase";

const MIN_LENGTH = 3;
const MAX_LENGTH = 32;

const EditCategoryPopup = ({ open, onClose, category = null, onUpdate, groupId }) => {
  const [categoryName, setCategoryName] = useState("");
  const [visibility, setVisibility] = useState("Public");
  const [isInitialized, setIsInitialized] = useState(false);

  const [originalName, setOriginalName] = useState("");
  const [originalVisibility, setOriginalVisibility] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const dispatch = useDispatch();

  const categoryType = category?.parentId ? "Subcategory" : "Category";
  useEffect(() => {
    if (open && category) {
      const name = category.title || "";
      const visibilityValue =
        category.visibility?.toLowerCase() === "internal" ? "Internal" : "Public";

      setCategoryName(name);
      setOriginalName(name);
      setVisibility(visibilityValue);
      setOriginalVisibility(visibilityValue);

      setSubmitError("");
      setIsInitialized(true);
    } else {
      setIsInitialized(false);
    }
  }, [open, category]);


  const validateCategory = () => {
    const trimmed = categoryName.trim();
    const trimmedLength = trimmed.length;

    if (trimmedLength === 0) {
      return "Category name is required.";
    }

    if (trimmedLength < MIN_LENGTH) {
      return `Category name must be at least ${MIN_LENGTH} characters.`;
    }

    if (trimmedLength > MAX_LENGTH) {
      return `Category name cannot exceed ${MAX_LENGTH} characters.`;
    }

    // Allow letters, numbers, space, -, _, ', (, )
    const allowedRegex = /^[a-zA-Z0-9\s\-_'\(\)]*$/;
    if (!allowedRegex.test(trimmed)) {
      return "Special characters are not allowed (only letters, numbers, spaces, -, _, ', (, and ) are permitted).";
    }

    return "";
  };

  const hasChanges =
    categoryName.trim() !== originalName.trim() ||
    visibility.toLowerCase() !== originalVisibility.toLowerCase();

  const buttonDisabled =
    !isInitialized || loading || !hasChanges;


  const handleUpdate = async () => {
    const validationError = validateCategory();

    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    // Clear error on successful validation
    setSubmitError("");

    setLoading(true);

    // Prepare the update payload
    const updatePayload = {
      categoryId: category?.id,
      name: categoryName.trim(),
      visibility: visibility.toLowerCase(),
      group_id: groupId,
    };

    // If this is a subcategory (has parentId), include it in the payload
    if (category?.parentId) {
      updatePayload.parentId = category.parentId;
    }

    try {
      // Wait for the update to complete
      await dispatch(updateKbCategory(updatePayload)).unwrap();

      // Wait for categories list to refresh
      await dispatch(getAllKbCategories({ group_id: groupId })).unwrap();

      // Update local state if onUpdate callback is provided
      if (onUpdate) {
        const updatedCategory = {
          ...category,
          title: categoryName.trim(),
          visibility: visibility.toLowerCase(),
        };
        onUpdate(updatedCategory);
      }

      // Close the dialog only after everything succeeds
      onClose();
    } catch (error) {
      setSubmitError(error?.message || "Failed to update category. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCategoryName("");
    setVisibility("Public");
    setOriginalName("");
    setOriginalVisibility("");
    setSubmitError("");
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      key={category?.id} // Forces remount when category changes
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      maxWidth="sm"
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "90vw", sm: "472px" },
          minWidth: { xs: "320px", sm: "472px" },
          maxWidth: { xs: "95vw", sm: "472px" },
          border: "1px solid #D5DCE1",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      <Box sx={{ p: "32px", display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb="8px">
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 500,
              color: "#061522",
              lineHeight: "18px",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Edit Category
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography
          variant="body2"
          sx={{
            mb: "27px",
            color: "#405261",
            fontSize: 14,
            fontWeight: 400,
            lineHeight: "22px",
          }}
        >
          Update the details of the selected category.
        </Typography>

        {/* Category Name Input */}
        <Box>
          <Input
            title={'Category Name'}
            placeholder={`Enter ${categoryType.toLowerCase()} name`}
            value={categoryName}
            onChange={(e) => {
              setCategoryName(e.target.value);
              if (submitError) setSubmitError("");
            }}
            error={submitError}
            autoFocus
          />
        </Box>

        {/* Visibility Select */}
        <FormControl size="small" sx={{ marginTop: "20px", width: "100%" }}>
          <Typography
            variant="body2"
            sx={{
              mb: "5px",
              color: "#405261",
              fontSize: 12,
              fontWeight: 400,
            }}
          >
            Visibility
          </Typography>
          <Select
            value={visibility}
            onChange={(e) => {
              setVisibility(e.target.value);
              if (submitError) setSubmitError("");
            }}
            IconComponent={Dropdown}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{ color: "#405261", fontSize: "14px" }}>Set status</span>;
              }
              return selected.charAt(0).toUpperCase() + selected.slice(1);
            }}
            sx={{
              height: "32px",
              width: "100%",
              fontSize: "14px",
              color: "#405261",
              "& .MuiSelect-select": { padding: "0px 12px" },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1px solid #D5DCE1",
                  marginTop: "4px",
                  borderRadius: "8px",
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    padding: "8px 16px",
                    color: "#405261",
                    "&:hover": { color: "#061522", background: "#F9FAFA", borderRadius: "8px" },
                    "&.Mui-selected": { color: "#061522", background: "#F9FAFA", borderRadius: "8px" },
                    "&.Mui-selected:hover": { background: "#F9FAFA" },
                  },
                },
              },
            }}
          >
            {["Public", "Internal"].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography
          variant="body2"
          sx={{
            color: "#405261",
            fontSize: 10,
            mt: "8px",
            lineHeight: "18px",
          }}
        >
          Hidden categories won't appear in the public knowledge base.
        </Typography>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Buttons */}
        <Box display="flex" gap="8px" mt="32px">
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={loading}
            sx={{
              textTransform: "none",
              borderColor: "#E5E7EB",
              color: "#405261",
              bgcolor: "#FFFFFF",
              borderRadius: "8px",
              flex: 1,
              height: "40px",
              fontSize: 14,
              fontWeight: 500,
              "&:hover": { borderColor: "#C5CED6", bgcolor: "#FFFFFF" },
              "&:disabled": { borderColor: "#E5E7EB", color: "#93A1B0" },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={buttonDisabled}
            sx={{
              textTransform: "none",
              bgcolor: "#148dfd",
              borderRadius: "8px",
              flex: 1,
              height: "40px",
              fontSize: 14,
              fontWeight: 500,
              boxShadow: "none",
              "&:hover": { bgcolor: "#0077cc" },
              "&:disabled": { bgcolor: "#F9FAFA", color: "#93A1B0" },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Update Category"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EditCategoryPopup;